import type { EncryptionImport } from "../types/imports";
import {
  EncryptionConfig,
  EncryptionCodeColour,
  DecodedEncryptionChannels,
  EncryptionMappings,
} from "../types/encryption";

const replaceChars: Record<string, string> = {
  "0": "#",
};
const fullCharMap = "#123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ%*=+";

function computeBitValues(bits: number[]) {
  const bitSum = bits.reduce<number[]>(
    (acc, _, i) => [...acc, i === 0 ? 0 : acc[i - 1] + bits[i - 1]],
    [],
  );
  const bitScale = bitSum.map((x) => 2 ** x);
  const bitMax = bits.map((x) => 2 ** x - 1);
  const bitMask = bitScale.map((x, i) => x * bitMax[i]);
  return { bitScale, bitMax, bitMask };
}

function renameChannel(channelName: string, maxValue: number) {
  if (channelName.startsWith("MF") && channelName.endsWith("DriverDefault")) {
    const mfPart = channelName.substring(
      0,
      channelName.indexOf("DriverDefault"),
    );
    const logValue = maxValue > 0 ? Math.floor(Math.log2(maxValue)) + 1 : 1;
    return `DD_${mfPart}_${logValue}`;
  }
  return channelName;
}

export function createConfig(
  encryptionImport: EncryptionImport,
): EncryptionConfig {
  const data = encryptionImport.data;

  // Rename channels
  const shortChannels = data.EncryptionShortIDMap.map((ch, i) =>
    renameChannel(ch, data.EncryptionShortMax[i]),
  );
  const longChannels = data.EncryptionLongIDMap.map((ch, i) =>
    renameChannel(ch, data.EncryptionLongMax[i]),
  );

  // Compute bit values
  const shortBits = computeBitValues(data.EncryptionShortBits);
  const longBits = computeBitValues(data.EncryptionLongBits);

  return {
    Green: {
      key: data.EncryptionShortKey,
      channels: shortChannels,
      wrapping: data.EncryptionShortValueWrapper,
      min: data.EncryptionShortMin,
      max: data.EncryptionShortMax,
      bits: data.EncryptionShortBits,
      bitScale: shortBits.bitScale,
      bitMask: shortBits.bitMask,
      bitMax: shortBits.bitMax,
      skipDenorm: data.EncryptionShortSkipDenorm,
      resolution: data.EncryptionShortBits.map(
        (x, i) =>
          (data.EncryptionShortMax[i] - data.EncryptionShortMin[i]) / 2 ** x,
      ),
      decimals: data.EncryptionShortDP,
      length: 4,
    },
    Orange: {
      key: data.EncryptionLongKey,
      channels: longChannels,
      wrapping: data.EncryptionLongValueWrapper,
      min: data.EncryptionLongMin,
      max: data.EncryptionLongMax,
      bits: data.EncryptionLongBits,
      bitScale: longBits.bitScale,
      bitMask: longBits.bitMask,
      bitMax: longBits.bitMax,
      skipDenorm: data.EncryptionLongSkipDenorm,
      resolution: data.EncryptionLongBits.map(
        (x, i) =>
          (data.EncryptionLongMax[i] - data.EncryptionLongMin[i]) / 2 ** x,
      ),
      decimals: data.EncryptionLongDP,
      length: 5,
    },
    mappings: {
      GOSwMapX: data.EncryptionGOSwMapX,
      GOSwMapY: data.EncryptionGOSwMapY,
    },
  };
}

export function decode(
  codeColour: EncryptionCodeColour,
  code: string,
  fullConfig: EncryptionConfig,
) {
  const config = fullConfig[codeColour];
  const mappings = fullConfig.mappings;

  for (const [char, replacement] of Object.entries(replaceChars)) {
    code = code.replaceAll(char, replacement);
  }

  const base10 = convertToBase10(code);
  if (base10 === null || code.length !== config.length) {
    return null;
  }

  // Encrypt_Decrypt2
  const XOR = base10 ^ config.key;

  // BitReverseTransform
  const AND = config.bitMask.map((x) => XOR & x);
  const transform1 = AND.map((x, i) => x / config.bitScale[i]);
  const transform2 = transform1.map(
    (x, i) => x / Math.max(1, config.bitMax[i]),
  );

  const ranges = config.max.map((max, i) => max - config.min[i]);

  // Denormalize (skip if skipDenorm[i] === 1)
  const transform3 = transform2.map((x, i) => {
    if (config.skipDenorm?.[i] === 1) return x;
    return Math.max(0, Math.min(1, x)) * ranges[i];
  });

  const transform4 = transform3.map((x, i) => {
    if (config.skipDenorm?.[i] === 1) return x;
    return x + config.min[i];
  });

  // Map to DecodedEncryptionChannels record
  const channels: DecodedEncryptionChannels = {};

  for (let i = 0; i < config.channels.length; i++) {
    const name = config.channels[i];
    if (name === "") break;

    const rounded = Number(transform4[i].toFixed(config.decimals[i]));

    // Apply GOSw mapping for NGOSw channel
    if (name === "NGOSw") {
      const mapped = mapNumericToString(rounded, mappings);
      channels[name] = mapped ?? rounded;
    } else {
      channels[name] = rounded;
    }
  }

  return channels;
}

export function convertToBase10(str: string) {
  const charMap = fullCharMap.slice(0, 40);
  const chars = str.toUpperCase().split("");
  if (chars.some((char) => !charMap.includes(char))) {
    return null;
  }
  return chars.reduce((result, char) => result * 40 + charMap.indexOf(char), 0);
}

export function convertToBase40(num: number) {
  const charMap = fullCharMap.slice(0, 40);
  if (!Number.isInteger(num) || num < 0) {
    return null;
  }
  if (num === 0) {
    return charMap[0];
  }
  const chars: string[] = [];
  while (num > 0) {
    chars.push(charMap[num % 40]);
    num = Math.floor(num / 40);
  }
  return chars.reverse().join("");
}

function mapNumericToString(
  numericValue: number,
  mappings: EncryptionMappings,
  mapType = "GOSw",
): string | null {
  if (!mappings || mapType !== "GOSw") return null;
  const idx = mappings.GOSwMapX?.indexOf(numericValue);
  return idx >= 0 ? String(mappings.GOSwMapY?.[idx]) ?? null : null;
}
